import { Dialog } from "@headlessui/react"
import React, { FC, useEffect, useState } from "react"
import { X } from "heroicons-react"
import { User, UserAdvance } from "../../../../../types"
import {
  currencyOptions,
  errorFilledToastOptions,
  successFilledToastOptions,
  USER_ADVANCE_TYPES,
  USER_ROLE
} from "../../../../../constants"
import { users } from "../../../../../apis"
import { mapListToCamelCase, mapToSnakeCase } from "../../../../../utility"
import { KButton, KDropdown, KInput, KSelectDate, KSpan, KTitleSpan, KTooltip } from "kahuna-base-react-components"
import { toast } from "react-toastify"
// @ts-ignore
import ApplyIcon from "../../../../../assets/admin_icons/apply.png"

type AdminUserAdvanceModalProps = {
  open: boolean
  onClose: () => void
  userId: number | undefined
  user: User | null
  onChangeCurrencyClick: () => void
  setSelectedCurrencyId: (selectedCurrencyId: number | undefined) => void
  isModal: boolean
  loggedInUser?: User
}

const advanceTypeOptions = [
  { label: "Direct Payment", value: 1, value2: USER_ADVANCE_TYPES.DIRECT_PAYMENT },
  { label: "Marketing", value: 2, value2: USER_ADVANCE_TYPES.MARKETING }
]

const AdminUserAdvanceModal: FC<AdminUserAdvanceModalProps> = (props) => {
  const { open } = props
  const [loading, setLoading] = useState(false)

  const [userAdvanceList, setUserAdvanceList] = useState<UserAdvance[]>([])
  const [userAdvanceToCreate, setUserAdvanceToCreate] = useState<UserAdvance>()
  const [addOpen, setAddOpen] = useState(false)
  const [existingCurrencyId, setExistingCurrencyId] = useState<number | undefined>(undefined)
  const [openUserAdvanceExist, setOpenUserAdvanceExist] = useState<boolean>(true)
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; userAdvance?: UserAdvance } | null>(null)

  const onContextMenuFeeTotal = (e: React.MouseEvent, userAdvance?: UserAdvance) => {
    e.preventDefault()
    setContextMenu({ x: e.clientX, y: e.clientY, userAdvance })
  }

  const closeContextMenu = () => setContextMenu(null)

  useEffect(() => {
    if (!contextMenu) return
    const handleClick = () => closeContextMenu()
    window.addEventListener("click", handleClick)
    return () => window.removeEventListener("click", handleClick)
  }, [contextMenu])

  const fetchUserAdvanceList = () => {
    users(`/fetch_user_advance/?user_id=${props.userId}`, "GET").then((resp) => {
      const advanceList: UserAdvance[] = mapListToCamelCase(resp.data.results)
      setUserAdvanceList(advanceList)

      const openAdvance = advanceList.some((advance: UserAdvance) => {
        const advanceTotal = advance?.advanceTotal ?? 0
        const paidTotal = advance?.paidTotal ?? 0

        if (Number(advanceTotal.toFixed(7)) - Number(paidTotal.toFixed(7)) > 0) {
          return true
        }
        return false
      })

      setOpenUserAdvanceExist(openAdvance)

      if (resp.data.results.length > 0) {
        const currencyId = resp.data.results[0].currency_id
        setExistingCurrencyId(currencyId)
        props.setSelectedCurrencyId(currencyId)
        setUserAdvanceToCreate({ currency: currencyId, originalCurrency: currencyId })
      }
    })
  }

  useEffect(() => {
    if (!(props.userId && open)) {
      setUserAdvanceList([])
      return
    }

    fetchUserAdvanceList()
  }, [props.userId, open])

  const onCloseModal = () => {
    setUserAdvanceList([])
    setUserAdvanceToCreate(undefined)
    setExistingCurrencyId(undefined)
    props.setSelectedCurrencyId(undefined)
    props.onClose()
    setAddOpen(false)
  }

  const saveUserAdvance = () => {
    /* All user advances currency for a user will be the same */
    setLoading(true)
    users(`/user_advance/`, "POST", { ...mapToSnakeCase(userAdvanceToCreate), user: props.userId })
      .then((resp) => {
        if (resp.data.success) {
          toast.success("User Advance Created Successfully.", successFilledToastOptions)
          fetchUserAdvanceList()
          setUserAdvanceToCreate(undefined)
        } else {
          toast.error("Error saving User Advance!.", errorFilledToastOptions)
        }
        setLoading(false)
      })
      .catch(() => {
        toast.error("Error saving User Advance!.", errorFilledToastOptions)
        setLoading(false)
      })
  }

  const updateUserAdvance = (userAdvance: UserAdvance, toDate?: Date, isStartDate?: boolean) => {
    const a = isStartDate ? userAdvance?.startDate?.substring(0, 10) : userAdvance?.endDate?.substring(0, 10)
    const b = toDate?.toISOString().substring(0, 10)

    if (a === b) {
      return
    }
    toDate?.setHours(9)

    const body: any = { id: userAdvance.id }
    if (isStartDate) {
      body["start_date"] = toDate
    } else {
      body["end_date"] = toDate
    }

    setLoading(true)
    users(`/update_user_advance/`, "PUT", { ...body })
      .then((resp) => {
        if (resp.data.success) {
          toast.success("User Advance Updated Successfully.", successFilledToastOptions)
          fetchUserAdvanceList()
        } else {
          toast.error("Error updating User Advance!.", errorFilledToastOptions)
        }
        setLoading(false)
      })
      .catch(() => {
        toast.error("Error updating User Advance!.", errorFilledToastOptions)
        setLoading(false)
      })
  }

  const onClickFeeTotal = (userAdvance?: UserAdvance) => {
    const feeTotal = window.prompt("Enter Fee Total: ")
    if (feeTotal === null) {
      return
    }

    setLoading(true)
    users(`/update_user_advance/`, "PUT", { id: userAdvance?.id, fee_total: feeTotal })
      .then((resp) => {
        if (resp.data.success) {
          toast.success("User Advance Updated Successfully.", successFilledToastOptions)
          fetchUserAdvanceList()
        } else {
          toast.error("Error updating User Advance!.", errorFilledToastOptions)
        }
        setLoading(false)
      })
      .catch(() => {
        toast.error("Error updating User Advance!.", errorFilledToastOptions)
        setLoading(false)
      })
  }

  const onApplyFeeTotal = (userAdvance?: UserAdvance) => {
    if (!window.confirm("Are you sure you want to apply the fee total to the advance total?")) {
      return
    }

    const advanceTotal = userAdvance?.advanceTotal || 0
    const paidTotal = userAdvance?.paidTotal || 0
    const feeTotal = userAdvance?.feeTotal || 0

    if (advanceTotal + feeTotal < paidTotal) {
      toast.error("Fee total cannot be greater than the remaining balance.", errorFilledToastOptions)
      return
    }

    setLoading(true)
    users(`/update_user_advance/`, "PUT", { id: userAdvance?.id, advance_total: advanceTotal + feeTotal })
      .then((resp) => {
        if (resp.data.success) {
          toast.success("User Advance Updated Successfully.", successFilledToastOptions)
          fetchUserAdvanceList()
        } else {
          toast.error("Error updating User Advance!.", errorFilledToastOptions)
        }
        setLoading(false)
      })
      .catch(() => {
        toast.error("Error updating User Advance!.", errorFilledToastOptions)
        setLoading(false)
      })
  }

  const deleteUserAdvance = (userAdvanceId: number) => {
    if (userAdvanceId === 0) {
      setAddOpen(false)
      return
    }

    if (!window.confirm("Are you sure?")) {
      return
    }

    setLoading(true)
    users(`/remove_user_advance/?id=${userAdvanceId}`, "DELETE")
      .then((resp) => {
        if (resp.data.success) {
          toast.success("User Advance Removed Successfully.", successFilledToastOptions)
        } else {
          toast.error(`Error removing User Advance! ${resp.data.message}`, errorFilledToastOptions)
        }
        fetchUserAdvanceList()
        setLoading(false)
      })
      .catch(() => {
        toast.error("Error removing User Advance!.", errorFilledToastOptions)
        setLoading(false)
      })
  }

  const onPayUserAdvance = (userAdvanceId: number) => {
    if (userAdvanceId === 0) {
      setAddOpen(false)
      return
    }

    if (!window.confirm("Sending money using REVOLUT! Are you sure?")) {
      return
    }

    setLoading(true)
    users(`/pay_user_advance/?id=${userAdvanceId}`, "POST")
      .then((resp) => {
        if (resp.data.success) {
          toast.success("Transfer sent to revolut successfully.", successFilledToastOptions)
        } else {
          toast.error(`Error transfering user advance! ${resp.data.message}`, {...errorFilledToastOptions, autoClose: 20000})
        }
        fetchUserAdvanceList()
        setLoading(false)
      })
      .catch(() => {
        toast.error("Error transfering user advance!.", errorFilledToastOptions)
        setLoading(false)
      })
  }

  const onSendUserAdvancePayEmail = (userAdvanceId: number) => {
    if (userAdvanceId === 0) {
      setAddOpen(false)
      return
    }

    if (!window.confirm("Sending EMAIL to artist! Are you sure?")) {
      return
    }

    setLoading(true)
    users(`/user_advance_pay_email/?id=${userAdvanceId}`, "PUT")
      .then((resp) => {
        if (resp.data.success) {
          toast.success("Email sent successfully.", successFilledToastOptions)
        } else {
          toast.error(`Error sending email! ${resp.data.message}`, errorFilledToastOptions)
        }
        fetchUserAdvanceList()
        setLoading(false)
      })
      .catch(() => {
        toast.error("Error sending email!.", errorFilledToastOptions)
        setLoading(false)
      })
  }

  const formatDate = (date?: Date) => {
    if (!date) {
      return undefined
    }

    const formatted =
      date.getFullYear() + String(date.getMonth() + 1).padStart(2, "0") + String(date.getDate()).padStart(2, "0")

    return formatted
  }

  const renderMain = () => {
    return (
      <div className="bg-white elev-b-sm rounded-lg overflow-hidden" style={{ padding: props.isModal ? 42 : 16 }}>
        <div className="flex flex-row items-center justify-between gap-1 mb-2">
          <div className="flex items-center gap-1">
            <KSpan text="User:" fontWeight={500} color="#000" />
            <KSpan text={`${props?.user?.firstName} ${props?.user?.lastName}`} color="#000" />
          </div>
          {existingCurrencyId &&
            (openUserAdvanceExist ? (
              <KTooltip
                backgroundColor="#000"
                padding="0px 4px"
                height="auto"
                arrowColor="#000"
                position="left"
                content={
                  <div className=" w-[220px] flex items-center py-2 px-2">
                    <KSpan
                      text={
                        "You cannot change the advance currency while there are advances that are not fully settled, i.e., those with a remaining balance."
                      }
                      fontSize={12}
                      lineHeight="16px"
                      color="#FFF"
                    />
                  </div>
                }
              >
                <div className="flex items-center">
                  <KButton text="Change Currency" disabled={true} onClick={props.onChangeCurrencyClick} />
                </div>
              </KTooltip>
            ) : (
              <div className="flex items-center">
                <KButton text="Change Currency" disabled={openUserAdvanceExist} onClick={props.onChangeCurrencyClick} />
              </div>
            ))}
        </div>{" "}
        <div className="mb-4">
          <KSpan text="You can update Advance Paid anytime. Select to edit. Advance will still be charged using Advance Approved date. Advance Paid is only for analytics" />
        </div>
        <div className={`flex justify-end`}>
          <X className="h-4 cursor-pointer mt-1" style={{ position: "absolute" }} onClick={() => onCloseModal()} />
        </div>
        <div className="flex mb-4 items-center">
          <div style={{ width: 140 }}>
            <KTitleSpan text={"Advance Start (Activity Period)"} fontSize={16} lineHeight="auto" />
          </div>
          <div style={{ width: 140 }}>
            <KTitleSpan text={"Advance Paid"} fontSize={16} lineHeight="auto" />
          </div>
          <div style={{ width: 120 }}>
            <KTitleSpan text={"Advance Total"} fontSize={16} lineHeight="auto" />
          </div>
          <div style={{ width: 100 }}>
            <KTitleSpan text={"Paid Total"} fontSize={16} lineHeight="auto" />
          </div>
          <div style={{ width: 100 }}>
            <KTitleSpan text={"Fee Total"} fontSize={16} lineHeight="auto" />
          </div>
          <div style={{ width: 140 }}>
            <KTitleSpan text={"Type"} fontSize={16} lineHeight="auto" />
          </div>
          <div style={{ width: 100 }}>
            <KTitleSpan text={"Currency"} fontSize={16} lineHeight="auto" />
          </div>
          <div style={{ width: 100 }}>
            <KTitleSpan text={"Original Currency"} fontSize={16} lineHeight="auto" />
          </div>
          <div style={{ width: 140 }}>
            <KTitleSpan text={"Created At"} fontSize={16} lineHeight="auto" />
          </div>
          <div style={{ width: 120 }} className="flex mr-2">
            <img width={24} src="/payment_icons/revolut.svg" />
            <KTitleSpan text={"Revolut"} fontSize={16} lineHeight="auto" />
          </div>
          <div style={{ width: 100 }}>
            <KTitleSpan text={"Action"} fontSize={16} lineHeight="auto" />
          </div>
        </div>
        <div className="flex flex-col gap-0 max-h-[50vh] overflow-auto">
          {userAdvanceList.map((userAdvance: UserAdvance, index: number) => {
            return (
              <div
                key={`user-advance-${index}`}
                className="flex mt-2 pb-2 items-center"
                style={{ width: "100%", borderBottom: "1px dashed grey" }}
              >
                <div className="flex gap-2 items-center" style={{ width: 140 }}>
                  <KSpan text={userAdvance?.startDate?.substring(0, 10) || "-"} />
                  <KSelectDate
                    icon="/analytics_icons/calendar.svg"
                    boxShadow={"none"}
                    hideBody={true}
                    value={userAdvance?.startDate ? new Date(userAdvance.startDate) : undefined}
                    onChange={(value) => updateUserAdvance(userAdvance, value, true)}
                  />
                </div>
                <div className="flex gap-2 items-center" style={{ width: 140 }}>
                  <KSpan text={userAdvance?.endDate?.substring(0, 10) || "-"} />
                  <KSelectDate
                    icon="/analytics_icons/calendar.svg"
                    boxShadow={"none"}
                    hideBody={true}
                    value={userAdvance?.endDate ? new Date(userAdvance.endDate) : undefined}
                    onChange={(value) => updateUserAdvance(userAdvance, value, false)}
                  />
                </div>
                <div style={{ width: 120 }}>
                  <KSpan
                    text={
                      userAdvance?.advanceTotal?.toLocaleString("en-EN", {
                        maximumFractionDigits: 2
                      }) || "-"
                    }
                  />
                </div>
                <div style={{ width: 100 }}>
                  <KSpan
                    text={
                      userAdvance?.paidTotal?.toLocaleString("en-EN", {
                        maximumFractionDigits: 2
                      }) || "-"
                    }
                  />
                </div>
                <div
                  style={{ width: 100, cursor: "pointer" }}
                  onContextMenu={(e) => onContextMenuFeeTotal(e, userAdvance)}
                  onDoubleClick={(e) => onContextMenuFeeTotal(e, userAdvance)}
                >
                  <KTooltip
                    width="100px"
                    position="left"
                    children={
                      <KSpan
                        text={
                          userAdvance?.feeTotal?.toLocaleString("en-EN", {
                            maximumFractionDigits: 2
                          }) || "-"
                        }
                      />
                    }
                    content={<KSpan text="Right click to open actions" />}
                  />
                </div>
                {contextMenu && (
                  <div
                    style={{
                      position: "fixed",
                      top: contextMenu.y,
                      left: contextMenu.x,
                      background: "#fff",
                      border: "1px solid #ddd",
                      borderRadius: 4,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                      zIndex: 1000,
                      minWidth: 120
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div
                      style={{ padding: "8px 12px", cursor: "pointer" }}
                      className="hover:bg-gray-100"
                      onClick={() => {
                        onClickFeeTotal(contextMenu.userAdvance)
                        closeContextMenu()
                      }}
                    >
                      <KSpan text="Edit Fee Total" />
                    </div>
                    <div
                      style={{ padding: "8px 12px", cursor: "pointer", borderTop: "1px dashed #ddd" }}
                      className="hover:bg-gray-100"
                      onClick={() => {
                        onApplyFeeTotal(contextMenu.userAdvance)
                        closeContextMenu()
                      }}
                    >
                      <KSpan text="Apply Advance Total" />
                    </div>
                  </div>
                )}
                <div style={{ width: 140 }}>
                  <KSpan
                    text={advanceTypeOptions.find((option) => option.value2 === userAdvance?.advanceType)?.label || "-"}
                  />
                </div>
                <div style={{ width: 100 }}>
                  <KSpan
                    text={currencyOptions.find((option) => option.value === userAdvance?.currencyId)?.label || "-"}
                  />
                </div>
                <div style={{ width: 100 }}>
                  <KSpan
                    text={
                      currencyOptions.find((option) => option.value === userAdvance?.originalCurrencyId)?.label || "-"
                    }
                  />
                </div>
                <div style={{ width: 140 }}>
                  <KSpan text={userAdvance?.created?.substring(0, 10) || "-"} />
                </div>
                <div style={{ width: 120 }}>
                  <KButton
                    dataTestId="pay-user-advance"
                    padding="8px"
                    height="36px"
                    width="80px"
                    text={userAdvance.transactionId ? "Paid" : "Pay"}
                    disabled={loading || userAdvance.transactionId ? true : false}
                    background="#FFF"
                    hoverBackground="#63636329"
                    activeBackground="#FFF"
                    onClick={() => onPayUserAdvance(userAdvance?.id || 0)}
                  />
                </div>
                <div style={{ width: 100 }} className="flex gap-4">
                  <KButton
                    dataTestId="send-user-advance-pay-email"
                    icon={"/account_icons/mail-black.svg"}
                    padding="8px"
                    height="36px"
                    disabled={loading || userAdvance.emailSent ? true : false}
                    width="36px"
                    background="#FFF"
                    hoverBackground="#63636329"
                    activeBackground="#FFF"
                    onClick={() => onSendUserAdvancePayEmail(userAdvance?.id || 0)}
                  />
                  <KButton
                    dataTestId="delete-user-advance"
                    icon={"/account_icons/trash_red.svg"}
                    padding="8px"
                    height="36px"
                    width="36px"
                    background="#FFF"
                    hoverBackground="#FFE6E8"
                    activeBackground="#FFF"
                    border="1px solid #FF5865"
                    hoverBorder="1px solid transparent"
                    activeBorder="1px solid #FF5865"
                    onClick={() => deleteUserAdvance(userAdvance?.id || 0)}
                  />
                </div>
              </div>
            )
          })}
        </div>
        {addOpen && (
          <div className="flex mt-8 mb-24" style={{ width: "100%" }}>
            <div className="pr-2 flex gap-2 items-center" style={{ width: 140 }}>
              <KSpan text={userAdvanceToCreate?.startDate || "-"} />
              <KSelectDate
                icon="/analytics_icons/calendar.svg"
                boxShadow={"none"}
                hideBody={true}
                value={userAdvanceToCreate?.startDate ? new Date(userAdvanceToCreate?.startDate) : undefined}
                onChange={(value) => {
                  setUserAdvanceToCreate({ ...userAdvanceToCreate, startDate: formatDate(value) })
                }}
              />
            </div>
            <div className="pr-2 flex gap-2 items-center" style={{ width: 140 }}>
              <KSpan text={userAdvanceToCreate?.endDate || "-"} />
              <KSelectDate
                icon="/analytics_icons/calendar.svg"
                boxShadow={"none"}
                hideBody={true}
                value={userAdvanceToCreate?.endDate ? new Date(userAdvanceToCreate?.endDate) : undefined}
                onChange={(value) => {
                  setUserAdvanceToCreate({ ...userAdvanceToCreate, endDate: formatDate(value) })
                }}
              />
            </div>
            <div className="pr-2" style={{ width: 140 }}>
              <KInput
                dataTestId="user-advance-total-input"
                placeholder="2000"
                value={userAdvanceToCreate?.advanceTotal?.toString() || ""}
                onChange={(value) => {
                  if (value) {
                    setUserAdvanceToCreate({ ...userAdvanceToCreate, advanceTotal: parseInt(value) })
                  } else {
                    setUserAdvanceToCreate({ ...userAdvanceToCreate, advanceTotal: 0 })
                  }
                }}
              />
            </div>
            <div style={{ width: 140 }}></div>
            <div className="pr-2" style={{ width: 140 }}>
              <KInput
                dataTestId="user-fee-total-input"
                placeholder="25"
                value={userAdvanceToCreate?.feeTotal?.toString() || ""}
                onChange={(value) => {
                  if (value) {
                    setUserAdvanceToCreate({ ...userAdvanceToCreate, feeTotal: parseInt(value) })
                  } else {
                    setUserAdvanceToCreate({ ...userAdvanceToCreate, feeTotal: 0 })
                  }
                }}
              />
            </div>
            <div className="pr-2" style={{ width: 140 }}>
              <KDropdown
                dataTestId="user-advance-type-dropdown"
                key={`${userAdvanceToCreate?.advanceType}`}
                placeholder="Direct Paym..."
                menuWidth={160}
                options={advanceTypeOptions}
                menuLeftMargin={-32}
                selected={advanceTypeOptions.find((option) => option.value2 === userAdvanceToCreate?.advanceType)}
                onSelect={(selected: any) =>
                  setUserAdvanceToCreate({ ...userAdvanceToCreate, advanceType: selected.value2 })
                }
                rightIcon="/admin_icons/caret-down.svg"
                enableIndicator
                isEllipsis
              />
            </div>

            <div className="pr-2 flex items-center" style={{ width: 100 }}>
              <div>
                {!existingCurrencyId ? (
                  <KDropdown
                    dataTestId="user-advance-currency-dropdown"
                    placeholder="TRY"
                    width={80}
                    menuWidth={80}
                    options={currencyOptions}
                    selected={currencyOptions.find((option) => option.value === userAdvanceToCreate?.currency)}
                    onSelect={(selected: any) =>
                      setUserAdvanceToCreate({
                        ...userAdvanceToCreate,
                        currency: selected.value,
                        originalCurrency: selected.value
                      })
                    }
                    rightIcon="/admin_icons/caret-down.svg"
                    enableIndicator
                  />
                ) : (
                  <KTooltip
                    backgroundColor="#000"
                    padding="0px 4px"
                    height="auto"
                    arrowColor="#000"
                    position="left"
                    content={
                      <div className=" w-[220px] flex items-center py-2 px-2">
                        <KSpan
                          text={
                            "You cannot select a currency different from the existing advances. If you need to make such a selection, please click the 'Change Currency' button to update the currency of the advances."
                          }
                          fontSize={12}
                          lineHeight="16px"
                          color="#FFF"
                        />
                      </div>
                    }
                  >
                    <KSpan text={currencyOptions.find((option) => option.value === existingCurrencyId)?.label || "-"} />
                  </KTooltip>
                )}
              </div>
            </div>
            <div style={{ width: 100 }} />
            <div style={{ width: 140 }} />
            <div style={{ width: 100 }} className="items-center flex">
              <KButton
                dataTestId="delete-user-advance-button"
                icon={"/account_icons/trash_red.svg"}
                padding="8px"
                height="36px"
                width="36px"
                background="#FFF"
                hoverBackground="#FFE6E8"
                activeBackground="#FFF"
                border="1px solid #FF5865"
                hoverBorder="1px solid transparent"
                activeBorder="1px solid #FF5865"
                onClick={() => deleteUserAdvance(0)}
              />
            </div>
          </div>
        )}
        <div className="mt-8">
          <KButton
            dataTestId="add-user-advance-button"
            background={addOpen ? "black" : "transparent"}
            textColor={addOpen ? "white" : "black"}
            shadowDisabled={true}
            width="auto"
            text={addOpen ? "Save Advance" : "+ Add New"}
            onClick={() => (addOpen ? saveUserAdvance() : setAddOpen(true))}
          />
        </div>
      </div>
    )
  }

  return props.isModal ? (
    <Dialog open={open} onClose={() => onCloseModal()} className="relative">
      <div className="fixed inset-0 bg-[rgba(0,0,0,0.3)]" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">{renderMain()}</div>
    </Dialog>
  ) : (
    renderMain()
  )
}

export default AdminUserAdvanceModal
